import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export function useActiveSubscription() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["active-subscription", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("subscriptions")
        .select("*, billing_plans(*)")
        .eq("user_id", user!.id)
        .in("status", ["active", "trialing"])
        .gt("current_period_end", new Date().toISOString())
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });
}

export interface Entitlements {
  plan_slug: string;
  limits: Record<string, number>;
  features: Record<string, boolean>;
}

const DEFAULT_ENTITLEMENTS: Entitlements = {
  plan_slug: "shareholder_free",
  limits: { ideas_per_month: 3, saved_grants: 10, ai_coach_msgs_per_day: 5, org_seats: 1 },
  features: {
    is_pro: false,
    grant_finder_export: false,
    legislation_alerts: false,
    ideas_analytics: false,
    funder_deep_view: false,
  },
};

export function useEntitlements() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["entitlements", user?.id],
    enabled: !!user,
    queryFn: async (): Promise<Entitlements> => {
      const { data, error } = await (supabase as any).rpc("get_user_entitlements", {
        _user_id: user!.id,
      });
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] : data) as Entitlements | undefined;
      return row ?? DEFAULT_ENTITLEMENTS;
    },
    staleTime: 60_000,
  });
}

export function useIsPro() {
  const { data } = useEntitlements();
  return Boolean(data?.features?.is_pro);
}

export function useIsPartnerPro() {
  const { data } = useEntitlements();
  return data?.plan_slug === "partner_hub_pro" || data?.plan_slug === "intelligence";
}

export function useFeatureGate(key: string): boolean {
  const { data } = useEntitlements();
  return Boolean(data?.features?.[key]);
}

/** Returns the numeric limit for a key; -1 means unlimited. Undefined while loading. */
export function useLimit(key: string): number | undefined {
  const { data } = useEntitlements();
  return data?.limits?.[key];
}